import { Search, Plus, CarFront, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { AppShellMock } from "./AppShellMock";

const MOCK_CARS = [
  {
    marca: "BMW",
    modelo: "320i Sport",
    chassi: "9BWZZZ377VT004251",
    ano: "2022",
    placa: "ABC1D23",
    cor: "Branco",
    categoria: "Sedan",
    km: "48.200",
    preco: "R$ 198.900",
    statusLabel: "Disponível",
    statusClass: "bg-slate-900 text-white border-slate-900 dark:bg-primary dark:text-primary-foreground dark:border-primary",
  },
  {
    marca: "Audi",
    modelo: "A4 Ambiente",
    chassi: "WAUZZZ8V5KA123456",
    ano: "2021",
    placa: "EFG4H56",
    cor: "Cinza",
    categoria: "Sedan",
    km: "62.100",
    preco: "R$ 234.500",
    statusLabel: "Reservado",
    statusClass: "bg-amber-500 text-white border-amber-500",
  },
] as const;

export default function MockGaragemShowcase() {
  return (
    <div
      className="pointer-events-none flex h-full min-h-[200px] select-none flex-col"
      aria-label="Prévia fictícia da Garagem (layout NoWey)"
    >
      <AppShellMock highlight="garagem">
        <div className="h-full min-h-0 overflow-y-auto overflow-x-hidden bg-background p-2 text-foreground">
          <header className="mb-3 space-y-2">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0 space-y-0.5">
                <h1 className="text-sm font-semibold tracking-tight text-slate-900 dark:text-foreground sm:text-base">
                  Garagem
                </h1>
                <p className="max-w-md text-[10px] font-normal text-slate-500 dark:text-muted-foreground">
                  Estoque, reservas e histórico de vendas em um só lugar.
                </p>
              </div>
              <div className="flex shrink-0 flex-col gap-0.5 sm:items-end">
                <span className="invisible h-[11px] text-[9px] select-none" aria-hidden>
                  .
                </span>
                <span className="inline-flex h-8 items-center gap-1.5 rounded-xl bg-[#1e3a8f] px-3 text-[10px] font-semibold text-white shadow-md shadow-blue-900/20">
                  <Plus className="h-3 w-3 shrink-0" aria-hidden />
                  Novo veículo
                </span>
              </div>
            </div>
          </header>

          <section className="mb-3 space-y-3 rounded-2xl border border-slate-200/80 bg-white p-3 shadow-[0_1px_3px_rgba(15,23,42,0.06)] dark:border-border dark:bg-card sm:p-4">
            <div className="relative w-full">
              <Search
                className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
                aria-hidden
              />
              <div className="h-9 w-full rounded-xl border border-slate-200/90 bg-slate-50/80 pl-9 text-[10px] leading-9 text-slate-400 dark:border-border dark:bg-muted/40">
                Buscar por modelo, marca ou placa…
              </div>
            </div>

            <div className="space-y-1.5">
              <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500 dark:text-muted-foreground">
                Status
              </p>
              <div className="flex flex-wrap items-center gap-1.5">
                {["Todos", "Preparação", "Disponível", "Reservado"].map((label, i) => {
                  const isPrep = label === "Preparação";
                  const isActive = i === 0;
                  return (
                    <span
                      key={label}
                      className={cn(
                        "shrink-0 rounded-full border px-2.5 py-1 text-[9px] font-medium",
                        isActive && !isPrep
                          ? "border-slate-900 bg-slate-900 text-white dark:border-primary dark:bg-primary dark:text-primary-foreground"
                          : isPrep
                            ? "border-purple-200/90 bg-purple-50/90 text-purple-900 dark:border-purple-800/60 dark:bg-purple-950/35 dark:text-purple-200"
                            : "border-slate-200/90 bg-slate-50 text-slate-600 dark:border-border dark:bg-muted/50 dark:text-muted-foreground"
                      )}
                    >
                      {label}
                    </span>
                  );
                })}
                <span className="ml-auto shrink-0 rounded-full border border-emerald-200/90 bg-emerald-50/90 px-2.5 py-1 text-[9px] font-semibold text-emerald-800 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-200">
                  Vendidos
                </span>
              </div>
            </div>
          </section>

          <div className="flex flex-col gap-2">
            {MOCK_CARS.map((car) => (
              <div
                key={car.placa}
                className="group relative flex w-full min-w-0 flex-col gap-2 rounded-2xl border border-border bg-card p-2.5 shadow-sm md:flex-row md:items-center md:gap-3 md:p-3"
              >
                <div className="relative h-24 w-full shrink-0 overflow-hidden rounded-2xl border border-border bg-secondary/50 md:h-20 md:w-36">
                  <div className="flex h-full w-full items-center justify-center bg-linear-to-br from-slate-200/40 to-slate-400/20 dark:from-slate-700/50 dark:to-slate-900/50">
                    <CarFront className="h-8 w-8 text-muted-foreground/60" aria-hidden />
                  </div>
                </div>

                <div className="min-w-0 flex-1">
                  <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[9px] md:grid-cols-3">
                    <div className="col-span-2 md:col-span-3">
                      <h3 className="truncate text-sm font-bold text-foreground md:text-base">
                        {car.marca} {car.modelo}
                      </h3>
                      <p className="mt-0.5 text-[8px] uppercase tracking-wide text-muted-foreground">
                        Chassi: {car.chassi} • Ano: {car.ano}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="shrink-0 text-[8px] font-bold uppercase text-muted-foreground/70">Placa</span>
                      <span className="font-semibold text-foreground">{car.placa}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="shrink-0 text-[8px] font-bold uppercase text-muted-foreground/70">Cor</span>
                      <span className="font-semibold text-foreground">{car.cor}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="shrink-0 text-[8px] font-bold uppercase text-muted-foreground/70">Km</span>
                      <span className="font-semibold text-foreground">{car.km}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="shrink-0 text-[8px] font-bold uppercase text-muted-foreground/70">Preço</span>
                      <span className="font-bold text-foreground">{car.preco}</span>
                    </div>
                  </div>
                </div>

                <div className="flex shrink-0 flex-col gap-1 md:items-end">
                  <span
                    className={cn(
                      "inline-flex items-center gap-1 rounded-full border-0 px-2.5 py-1 text-[9px] font-black whitespace-nowrap",
                      car.statusClass
                    )}
                  >
                    {car.statusLabel}
                    <ChevronDown className="h-3 w-3 opacity-80" aria-hidden />
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full border-0 bg-slate-100 px-2.5 py-1 text-[9px] font-black text-slate-800 dark:bg-muted dark:text-foreground">
                    Próprio
                    <ChevronDown className="h-3 w-3 opacity-80" aria-hidden />
                  </span>
                </div>
              </div>
            ))}
          </div>

          <p className="mt-2 text-[8px] text-muted-foreground">Interface de demonstração — dados fictícios.</p>
        </div>
      </AppShellMock>
    </div>
  );
}
